// taken from Dawn and modified, added dnd support

const attributes = {
  slider: "data-slider-component-slider",
  sliderItem: "data-slider-component-slider-item",
  prevButton: "data-slider-component-prev",
  nextButton: "data-slider-component-next",
  dot: "data-slider-component-dot",
  dotTemplate: "data-slider-component-dot-template",
  currentPage: "data-slider-component-current-page",
  totalPages: "data-slider-component-total-pages",
};

class CustomSlider extends HTMLElement {
  #handleDragBound;

  constructor() {
    super();
    this.#handleDragBound = this.#handleDrag.bind(this);
    this.slider = this.querySelector(`[${attributes.slider}]`);
    this.sliderItems = this.querySelectorAll(`[${attributes.sliderItem}]`);
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(`[${attributes.currentPage}]`);
    this.pageTotalElement = this.querySelector(`[${attributes.totalPages}]`);
    this.prevButton = this.querySelector(`[${attributes.prevButton}]`);
    this.nextButton = this.querySelector(`[${attributes.nextButton}]`);
    this.dotGroupList = [];

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.#initDragAndDrop();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.#initDots();
    this.update();
    this.updateScrollableAttributes();
  }

  // Drag-to-scroll is a mouse affordance. Touch gestures can still raise the
  // compatibility mouse events, and #handleDrag reacts by yanking scrollLeft a
  // whole slide away from wherever the fling had got to — a programmatic scroll
  // to an arbitrary mid-gesture offset, which cancels the fling and leaves the
  // scroller resting between snap points. Gate it on the real pointer type.
  #initDragAndDrop() {
    this.slider?.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse") return;
      document.addEventListener("mousemove", this.#handleDragBound);
    });
    ["pointerup", "pointercancel"].forEach((eventName) => {
      this.slider?.addEventListener(eventName, () => {
        document.removeEventListener("mousemove", this.#handleDragBound);
      });
    });
  }

  #handleDrag(event) {
    if (event.movementX !== 0) {
      event.preventDefault();
      const step = 1;
      this.slideScrollPosition =
        event.movementX < 0
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    }
    document.removeEventListener("mousemove", this.#handleDragBound);
  }

  #initDots() {
    this.dotGroupList = [];
    this.querySelectorAll(`[${attributes.dotTemplate}]`).forEach(
      ($dotTemplate) => {
        const dotGroup = [];
        const $dotContainer = $dotTemplate.parentElement;
        if (!$dotContainer) return;
        const $dotList = $dotContainer.querySelectorAll(`[${attributes.dot}]`);
        for (
          let i = 0;
          i < Math.max($dotList.length, this.sliderItemsToShow?.length || 0);
          i++
        ) {
          if (
            (!this.sliderItemsToShow?.[i] && $dotList[i]) ||
            this.totalPages === 1
          ) {
            $dotList[i]?.remove();
          } else if (this.sliderItemsToShow?.[i] && !$dotList[i]) {
            const $dot = $dotTemplate.content.firstElementChild.cloneNode(true);
            const dotTemplateClass =
              $dotTemplate.getAttribute(attributes.dotTemplate) || "";
            $dot.setAttribute(attributes.dot, dotTemplateClass);
            $dotContainer.appendChild($dot);
            dotGroup.push($dot);
          } else {
            dotGroup.push($dotList[i]);
          }
        }
        this.dotGroupList.push(dotGroup);
      }
    );
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll(`[${attributes.sliderItem}]`);
    this.initPages();
  }

  updateScrollableAttributes() {
    if (!this.slider || !this.sliderItemsToShow || this.sliderItemsToShow.length < 2) {
      this.removeAttribute('scrollable-left');
      this.removeAttribute('data-scrollable-right');
      return;
    }

    // Check if can scroll left (not at beginning)
    const canScrollLeft = this.slider.scrollLeft > 0;
    
    // Check if can scroll right (not at end)
    const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
    const canScrollRight = this.slider.scrollLeft < maxScrollLeft;

    // Set attributes based on scroll state
    if (canScrollLeft) {
      this.setAttribute('scrollable-left', '');
    } else {
      this.removeAttribute('scrollable-left');
    }

    if (canScrollRight) {
      this.setAttribute('scrollable-right', '');
    } else {
      this.removeAttribute('scrollable-right');
    }
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    // Update scrollable attributes on component root
    this.updateScrollableAttributes();

    this.dotGroupList.forEach((dotGroup) => {
      dotGroup.forEach(($dot, index) => {
        const rangeMin = this.currentPage - 1;
        const rangeMax = rangeMin + this.slidesPerPage;

        if (index >= rangeMin && index < rangeMax) {
          $dot.classList.toggle($dot.getAttribute(attributes.dot), true);
        } else {
          $dot.classList.toggle($dot.getAttribute(attributes.dot), false);
        }
      });
    });

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton?.setAttribute("disabled", "disabled");
    } else {
      this.prevButton?.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton?.setAttribute("disabled", "disabled");
    } else {
      this.nextButton?.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.hasAttribute(
      attributes.nextButton
    )
      ? this.slider.scrollLeft + step * this.sliderItemOffset
      : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  // Smooth is requested here rather than via CSS `scroll-behavior`, which would
  // also apply to touch flings — and on a mandatory snap container that stops
  // iOS Safari re-snapping once the fling ends.
  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
      behavior: "smooth",
    });
  }
}

if (!customElements.get('custom-slider')) {
  customElements.define("custom-slider", CustomSlider);
}